import type { TenantRole } from "../api/types";

// Capacidades usadas para montar menus e habilitar ações. Autorização visual
// não substitui a autorização da API: o backend revalida cada operação.
export type Capability =
  | "profile.view"
  | "profile.edit"
  | "admins.view"
  | "admins.manage"
  | "admins.manage_owner"
  | "structure.view"
  | "structure.edit"
  | "people.view"
  | "people.edit"
  | "occupancies.view"
  | "occupancies.edit"
  | "invitations.view"
  | "invitations.manage"
  | "devices.view"
  | "devices.revoke"
  | "imports.run"
  | "audit.view";

const ALL: Capability[] = [
  "profile.view", "profile.edit",
  "admins.view", "admins.manage", "admins.manage_owner",
  "structure.view", "structure.edit",
  "people.view", "people.edit",
  "occupancies.view", "occupancies.edit",
  "invitations.view", "invitations.manage",
  "devices.view", "devices.revoke",
  "imports.run",
  "audit.view",
];

const ROLE_CAPABILITIES: Record<TenantRole, ReadonlySet<Capability>> = {
  // Acesso completo, incluindo gestão de outros tenant_owner.
  tenant_owner: new Set(ALL),
  // Cadastro completo, mas não altera tenant_owner.
  tenant_admin: new Set(ALL.filter((c) => c !== "admins.manage_owner")),
  // Estrutura, pessoas, ocupações e convites; não administra administradores.
  building_manager: new Set([
    "profile.view",
    "structure.view", "structure.edit",
    "people.view", "people.edit",
    "occupancies.view", "occupancies.edit",
    "invitations.view", "invitations.manage",
  ]),
  // Consulta operacional: unidades, pessoas e situação dos convites.
  concierge_operator: new Set([
    "profile.view",
    "structure.view",
    "people.view",
    "occupancies.view",
    "invitations.view",
  ]),
  // Somente leitura e auditoria.
  auditor: new Set([
    "profile.view",
    "structure.view",
    "people.view",
    "occupancies.view",
    "invitations.view",
    "devices.view",
    "audit.view",
  ]),
};

export function can(role: TenantRole, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role].has(capability);
}

export type NavItem = {
  path: string;
  label: string;
  capability: Capability | null;
};

export const NAV_ITEMS: NavItem[] = [
  { path: "/", label: "Visão geral", capability: null },
  { path: "/perfil", label: "Perfil do condomínio", capability: "profile.view" },
  { path: "/usuarios", label: "Usuários administrativos", capability: "admins.view" },
  { path: "/estrutura", label: "Blocos e unidades", capability: "structure.view" },
  { path: "/pessoas", label: "Pessoas", capability: "people.view" },
  { path: "/ocupacoes", label: "Ocupações", capability: "occupancies.view" },
  { path: "/convites", label: "Convites do app", capability: "invitations.view" },
  { path: "/dispositivos", label: "Dispositivos", capability: "devices.view" },
  { path: "/importacao", label: "Importação", capability: "imports.run" },
  { path: "/auditoria", label: "Auditoria", capability: "audit.view" },
];

export function visibleNavItems(role: TenantRole): NavItem[] {
  return NAV_ITEMS.filter((item) => item.capability === null || can(role, item.capability));
}
